// src/components/PhotoSharing/PhotoSharingModal.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   Button,
   Box,
   Typography,
   Grid,
   Card,
   CardMedia,
   Checkbox,
   TextField,
   Tab,
   Tabs,
   Chip,
   Alert,
   CircularProgress,
   FormControlLabel,
   Switch,
   IconButton,
   Tooltip,
   Paper,
   Divider,
   Select,
   MenuItem,
   FormControl,
   InputLabel,
   OutlinedInput,
   ListItemText,
   Snackbar,
} from '@mui/material';
import {
   Close as CloseIcon,
   Email as EmailIcon,
   People as PeopleIcon,
   PersonAdd as PersonAddIcon,
   Delete as DeleteIcon,
   Send as SendIcon,
   ContentCopy as ContentCopyIcon,
   Link as LinkIcon,
} from '@mui/icons-material';
import {
   PhotoSharingRecipient,
   SelectedPhoto,
   LayoutType,
   LAYOUT_OPTIONS,
   PhotoSharingData,
   PhotoSharingResponse,
} from '../../types/photoSharing';
import { PhotoSharingService } from '../../services/photoSharingService';
import { useCanSharePhotos, EntityData } from '../../utils/photoSharingPermissions';
import { trackUserAction, ACTION_TYPES, ACTION_CATEGORIES } from '../../utils/actionTracker';
import { useTheme } from '@mui/material/styles';

interface PhotoSharingModalProps {
   open: boolean;
   onClose: () => void;
   entityType: 'trip' | 'event' | 'album';
   entityId: string;
   entityTitle: string;
   selectedPhotos?: any[];
   entityData?: EntityData | null;
   onSuccess?: (result: any) => void;
}

interface TabPanelProps {
   children?: React.ReactNode;
   index: number;
   value: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
   return (
      <div
         role='tabpanel'
         hidden={value !== index}
         id={`photo-sharing-tabpanel-${index}`}
         aria-labelledby={`photo-sharing-tab-${index}`}
         {...other}
      >
         {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
      </div>
   );
}

const PhotoSharingModal: React.FC<PhotoSharingModalProps> = ({
   open,
   onClose,
   entityType,
   entityId,
   entityTitle,
   selectedPhotos: preselectedPhotos,
   entityData,
   onSuccess,
}) => {
   const theme = useTheme();

   // State management
   const [currentStep, setCurrentStep] = useState<'photos' | 'layout' | 'preview' | 'recipients'>(
      'photos'
   );
   const [photos, setPhotos] = useState<SelectedPhoto[]>([]);
   const [selectedPhotos, setSelectedPhotos] = useState<SelectedPhoto[]>([]);
   const [selectedLayout, setSelectedLayout] = useState<LayoutType>('grid-square');
   const [participants, setParticipants] = useState<PhotoSharingRecipient[]>([]);
   const [selectedParticipants, setSelectedParticipants] = useState<Set<string>>(new Set());
   const [nonParticipants, setNonParticipants] = useState<PhotoSharingRecipient[]>([]);
   const [recipientTab, setRecipientTab] = useState(0); // 0 = participants, 1 = non-participants
   const [newEmail, setNewEmail] = useState('');
   const [newDisplayName, setNewDisplayName] = useState('');
   const [message, setMessage] = useState('');
   const [subject, setSubject] = useState('');
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState<string | null>(null);

   /** Social link first — user can switch to email. */
   const [shareDelivery, setShareDelivery] = useState<'social' | 'email'>('social');
   const [socialSuccess, setSocialSuccess] = useState<{ shareToken: string } | null>(null);
   const [copyToastOpen, setCopyToastOpen] = useState(false);
   const lastShareResultRef = useRef<PhotoSharingResponse | null>(null);

   // Helper function to get max photos for a layout
   const getMaxPhotosForLayout = (layout: LayoutType): number => {
      const layoutOption = LAYOUT_OPTIONS.find((l) => l.id === layout);
      return layoutOption?.maxPhotos || 6;
   };

   // Helper functions for participant selection
   const toggleParticipantSelection = (participantId: string) => {
      setSelectedParticipants((prev) => {
         const newSet = new Set(prev);
         if (newSet.has(participantId)) {
            newSet.delete(participantId);
         } else {
            newSet.add(participantId);
         }
         return newSet;
      });
   };

   const selectAllParticipants = () => {
      const allIds = new Set(participants.map((p) => p.id));
      setSelectedParticipants(allIds);
   };

   const deselectAllParticipants = () => {
      setSelectedParticipants(new Set());
   };

   // Helper function to get grid configuration for layout previews
   const getLayoutGridConfig = (layout: LayoutType) => {
      switch (layout) {
         case 'grid-square':
            return { columns: 'repeat(3, 1fr)', rows: 'repeat(2, 1fr)' }; // Square grid preview
         case 'grid-instagram':
            return { columns: 'repeat(3, 1fr)', rows: 'repeat(2, 1fr)' }; // Instagram 3x3 preview
         case 'masonry-pinterest':
            return { columns: 'repeat(3, 1fr)', rows: 'masonry' }; // Pinterest masonry
         case 'single-hero':
            return { columns: '1fr', rows: '1fr' }; // Single large photo
         case 'story-horizontal':
            return { columns: 'repeat(4, 1fr)', rows: '1fr' }; // Horizontal story strip
         case 'collage-puzzle':
            return { columns: 'repeat(3, 1fr)', rows: 'repeat(2, 1fr)' }; // Puzzle pieces
         case 'asymmetrical-modern':
            return { columns: '2fr 1fr 1fr', rows: 'repeat(2, 1fr)' }; // Asymmetrical layout
         case 'bordered-classic':
            return { columns: 'repeat(2, 1fr)', rows: 'repeat(2, 1fr)' }; // Classic bordered
         case 'magazine-editorial':
            return { columns: '2fr 1fr', rows: '1fr 1fr 0.5fr' }; // Magazine spread
         case 'mosaic-creative':
            return { columns: 'repeat(4, 1fr)', rows: 'repeat(2, auto)' }; // Creative mosaic
         default:
            return { columns: 'repeat(2, 1fr)', rows: 'repeat(2, 1fr)' };
      }
   };

   // Define loadPhotosAndParticipants function before useEffect
   useEffect(() => {
      if (!open) return;
      setSocialSuccess(null);
      setShareDelivery('social');
      lastShareResultRef.current = null;
      trackUserAction({
         actionType: ACTION_TYPES.PHOTO_SHARE_MODAL_OPENED,
         category: ACTION_CATEGORIES.SOCIAL,
         description: 'Photo sharing modal opened',
         metadata: {
            entityType,
            entityId,
            entityTitle,
         },
      }).catch(() => {});
   }, [open, entityType, entityId, entityTitle]);

   const loadPhotosAndParticipants = async () => {
      setLoading(true);
      setError(null);
      try {
         // If photos are preselected, use them directly without fetching from backend
         if (preselectedPhotos && preselectedPhotos.length > 0) {
            // Use preselected photos directly
            setPhotos(preselectedPhotos);
            setSelectedPhotos(preselectedPhotos);

            // For personal photos (album type with personal-photos ID), skip participant fetching
            if (entityType === 'album' && entityId === 'personal-photos') {
               // Personal photos don't have participants - user shares with anyone they want
               setParticipants([]);
               setNonParticipants([]);
            } else {
               // Only fetch participants for real entities (trips/events)
               const participantsResponse = await PhotoSharingService.getParticipants(
                  entityType,
                  entityId
               );

               setParticipants(participantsResponse.participants);
               setNonParticipants(participantsResponse.nonParticipants);
            }
         } else {
            // No preselected photos, fetch everything from backend
            const [photosResponse, participantsResponse] = await Promise.all([
               PhotoSharingService.getPhotos(entityType, entityId),
               PhotoSharingService.getParticipants(entityType, entityId),
            ]);

            setPhotos(photosResponse.photos);
            setParticipants(participantsResponse.participants);
            setNonParticipants(participantsResponse.nonParticipants);
         }
      } catch (err: any) {
         setError(err.message || 'Failed to load data');
      } finally {
         setLoading(false);
      }
   };

   // Load data when modal opens
   useEffect(() => {
      if (open) {
         loadPhotosAndParticipants();
         setSubject(`Photos from ${entityTitle}`);
      }
   }, [open, entityType, entityId, entityTitle, preselectedPhotos]);

   // Check user permissions after all hooks
   const canShare = useCanSharePhotos(entityData || null);

   // Don't render modal if user can't share (is viewer)
   if (!canShare) {
      return null;
   }

   // Photo selection handlers
   const togglePhotoSelection = (photo: SelectedPhoto) => {
      setSelectedPhotos((prev) => {
         const isSelected = prev.some((p) => p.id === photo.id);
         if (isSelected) {
            // Remove from selection
            const newSelection = prev.filter((p) => p.id !== photo.id);
            // If photos were preselected, also remove from the photos list (so it disappears from modal)
            if (preselectedPhotos && preselectedPhotos.length > 0) {
               setPhotos((prevPhotos) => prevPhotos.filter((p) => p.id !== photo.id));
            }
            return newSelection;
         } else {
            // Add to selection
            const newPhoto = { ...photo, selected: true };
            return [...prev, newPhoto];
         }
      });
      setError(null); // Clear any errors when user makes a selection
   };

   const selectAllPhotos = () => {
      if (selectedPhotos.length === photos.length) {
         // Deselect all
         setSelectedPhotos([]);
      } else {
         // Select all
         setSelectedPhotos(photos.map((photo) => ({ ...photo, selected: true })));
      }
      setError(null); // Clear any errors when user makes a selection
   };

   // Layout validation
   const validateLayoutSelection = () => {
      const validation = PhotoSharingService.validatePhotoCount(
         selectedLayout,
         selectedPhotos.length
      );
      if (!validation.valid) {
         setError(validation.message || 'Invalid photo count for selected layout');
         return false;
      }
      setError(null);
      return true;
   };

   // Non-participant management
   const addNonParticipant = () => {
      if (!newEmail.trim()) {
         setError('Email is required');
         return;
      }

      if (!PhotoSharingService.validateEmail(newEmail)) {
         setError('Please enter a valid email address');
         return;
      }

      const existingEmails = [
         ...participants.map((p) => p.email),
         ...nonParticipants.map((p) => p.email),
      ];

      if (existingEmails.includes(newEmail.toLowerCase())) {
         setError('This email is already added');
         return;
      }

      const newRecipient: PhotoSharingRecipient = {
         id: `non-participant-${Date.now()}`,
         email: newEmail.toLowerCase(),
         displayName: newDisplayName.trim() || newEmail.split('@')[0],
         isParticipant: false,
      };

      setNonParticipants((prev) => [...prev, newRecipient]);
      setNewEmail('');
      setNewDisplayName('');
      setError(null);
   };

   const removeNonParticipant = (recipientId: string) => {
      setNonParticipants((prev) => prev.filter((r) => r.id !== recipientId));
   };

   // Step navigation
   const handleNext = () => {
      if (currentStep === 'photos') {
         if (selectedPhotos.length === 0) {
            setError('Please select at least one photo');
            return;
         }
         setCurrentStep('layout');
         setError(null);
      } else if (currentStep === 'layout') {
         if (!validateLayoutSelection()) {
            return;
         }
         setCurrentStep('preview');
      } else if (currentStep === 'preview') {
         setCurrentStep('recipients');
      }
   };

   const handleBack = () => {
      if (currentStep === 'layout') {
         setCurrentStep('photos');
      } else if (currentStep === 'preview') {
         setCurrentStep('layout');
      } else if (currentStep === 'recipients') {
         setCurrentStep('preview');
      }
      setError(null);
   };

   /** Collection already contains only selected photos; no ?photos= needed (shorter URLs). */
   const buildSocialCollectionUrl = (shareToken: string) => {
      return `${window.location.origin}/shared/collection/${shareToken}`;
   };

   /** UTM-tagged link for traffic source analytics (see record-open). */
   const buildTrackedSocialCollectionUrl = (
      shareToken: string,
      utmSource: string,
      utmMedium = 'social',
   ) => {
      const u = new URL(`${window.location.origin}/shared/collection/${shareToken}`);
      u.searchParams.set('utm_source', utmSource);
      u.searchParams.set('utm_medium', utmMedium);
      return u.toString();
   };

   const handleCopySocialLink = async (url: string) => {
      try {
         await navigator.clipboard.writeText(url);
         setCopyToastOpen(true);
         trackUserAction({
            actionType: ACTION_TYPES.PHOTO_SHARE_LINK_COPIED,
            category: ACTION_CATEGORIES.SOCIAL,
            description: 'Copied social collection share link',
            metadata: { entityType, entityId, entityTitle },
         }).catch(() => {});
      } catch {
         try {
            const ta = document.createElement('textarea');
            ta.value = url;
            ta.style.position = 'fixed';
            ta.style.left = '-9999px';
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
            setCopyToastOpen(true);
            trackUserAction({
               actionType: ACTION_TYPES.PHOTO_SHARE_LINK_COPIED,
               category: ACTION_CATEGORIES.SOCIAL,
               description: 'Copied social collection share link (fallback)',
               metadata: { entityType, entityId, entityTitle },
            }).catch(() => {});
         } catch {
            setError('Could not copy. Select the link and copy manually.');
         }
      }
   };

   // Share photos
   const handleShare = async () => {
      setLoading(true);
      setError(null);

      try {
         if (shareDelivery === 'social') {
            const sharingData: PhotoSharingData = {
               selectedPhotos,
               layout: selectedLayout,
               recipients: { participants: [], nonParticipants: [] },
               message: message.trim(),
               subject: subject.trim() || `Photos from ${entityTitle}`,
            };

            const result = await PhotoSharingService.sharePhotos({
               entityType,
               entityId,
               entityTitle,
               sharingData,
               createdBy: '',
               createdAt: new Date(),
            });

            const token = result.shareToken;
            if (!token) {
               throw new Error('Server did not return a share link. Try again.');
            }

            lastShareResultRef.current = result;
            setSocialSuccess({ shareToken: token });
            trackUserAction({
               actionType: ACTION_TYPES.PHOTO_SHARE_LINK_CREATED,
               category: ACTION_CATEGORIES.SOCIAL,
               description: 'Created social share link for photo collection',
               metadata: {
                  entityType,
                  entityId,
                  entityTitle,
                  sharedCollectionId: result.sharedCollectionId,
               },
            }).catch(() => {});
            return;
         }

         const selectedParticipantsList = participants.filter((p) =>
            selectedParticipants.has(p.id),
         );

         if (selectedParticipantsList.length === 0 && nonParticipants.length === 0) {
            setError('Please select at least one recipient');
            setLoading(false);
            return;
         }

         const sharingData: PhotoSharingData = {
            selectedPhotos,
            layout: selectedLayout,
            recipients: {
               participants: selectedParticipantsList,
               nonParticipants,
            },
            message: message.trim(),
            subject: subject.trim() || `Photos from ${entityTitle}`,
         };

         const result = await PhotoSharingService.sharePhotos({
            entityType,
            entityId,
            entityTitle,
            sharingData,
            createdBy: '',
            createdAt: new Date(),
         });

         trackUserAction({
            actionType: ACTION_TYPES.PHOTO_SHARE_EMAIL_SENT,
            category: ACTION_CATEGORIES.SOCIAL,
            description: 'Sent photo collection via email',
            metadata: { entityType, entityId, entityTitle },
         }).catch(() => {});

         onSuccess?.(result);
         onClose();
      } catch (err: unknown) {
         const msg = err instanceof Error ? err.message : 'Failed to share photos';
         setError(msg);
      } finally {
         setLoading(false);
      }
   };

   const handleDoneSocialSuccess = () => {
      if (lastShareResultRef.current) {
         onSuccess?.(lastShareResultRef.current as PhotoSharingResponse);
      }
      setSocialSuccess(null);
      setCurrentStep('photos');
      setSelectedPhotos([]);
      setSelectedLayout('grid-square');
      setSelectedParticipants(new Set());
      setNonParticipants([]);
      setMessage('');
      setNewEmail('');
      setNewDisplayName('');
      setError(null);
      onClose();
   };

   // Reset modal state when closing
   const handleClose = () => {
      setSocialSuccess(null);
      setShareDelivery('social');
      lastShareResultRef.current = null;
      setCurrentStep('photos');
      setSelectedPhotos([]);
      setSelectedLayout('grid-square');
      setSelectedParticipants(new Set());
      setNonParticipants([]);
      setMessage('');
      setNewEmail('');
      setNewDisplayName('');
      setError(null);
      onClose();
   };

   if (loading && photos.length === 0) {
      return (
         <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
            <DialogContent>
               <Box display='flex' justifyContent='center' alignItems='center' minHeight='200px'>
                  <CircularProgress />
               </Box>
            </DialogContent>
         </Dialog>
      );
   }

   return (
      <Dialog
         open={open}
         onClose={handleClose}
         maxWidth='lg'
         fullWidth
         PaperProps={{ sx: { height: '90vh' } }}
      >
         <DialogTitle>
            <Box display='flex' alignItems='flex-start' justifyContent='space-between' gap={1}>
               <Box>
                  <Typography variant='h6'>Social share — {entityTitle}</Typography>
                  {!socialSuccess && (
                     <Typography variant='caption' color='text.secondary' component='div' sx={{ mt: 0.5 }}>
                        Step{' '}
                        {currentStep === 'photos'
                           ? 1
                           : currentStep === 'layout'
                             ? 2
                             : currentStep === 'preview'
                               ? 3
                               : 4}{' '}
                        of 4 —{' '}
                        {currentStep === 'photos'
                           ? 'Select photos'
                           : currentStep === 'layout'
                             ? 'Choose layout'
                             : currentStep === 'preview'
                               ? 'Preview'
                               : 'Link or email'}
                     </Typography>
                  )}
               </Box>
               <IconButton onClick={handleClose} size='small' sx={{ mt: -0.5 }}>
                  <CloseIcon />
               </IconButton>
            </Box>
         </DialogTitle>

         <DialogContent dividers>
            {socialSuccess ? (
               <Box sx={{ py: 1 }}>
                  <Alert severity='success' sx={{ mb: 2 }}>
                     Copy a tracked link for each app so analytics can show where opens came from, or
                     copy the plain link.
                  </Alert>
                  <TextField
                     fullWidth
                     value={buildSocialCollectionUrl(socialSuccess.shareToken)}
                     label='Plain link (no tracking)'
                     InputProps={{ readOnly: true }}
                     size='small'
                     sx={{
                        mb: 2,
                        '& input': { fontFamily: 'monospace', fontSize: 13 },
                     }}
                  />
                  <Button
                     fullWidth
                     variant='outlined'
                     color='primary'
                     size='medium'
                     startIcon={<ContentCopyIcon />}
                     onClick={() =>
                        void handleCopySocialLink(buildSocialCollectionUrl(socialSuccess.shareToken))
                     }
                     sx={{ mb: 2 }}
                  >
                     Copy plain link
                  </Button>
                  <Typography variant='subtitle2' sx={{ mb: 1 }}>
                     Tracked links (recommended)
                  </Typography>
                  <Grid container spacing={1} sx={{ mb: 1 }}>
                     {(
                        [
                           ['Instagram', 'instagram'],
                           ['WhatsApp', 'whatsapp'],
                           ['Messages', 'messages'],
                           ['SMS', 'sms'],
                           ['Facebook', 'facebook'],
                           ['Email', 'email'],
                        ] as const
                     ).map(([label, utm]) => (
                        <Grid item xs={6} sm={4} key={utm}>
                           <Button
                              fullWidth
                              variant='outlined'
                              size='small'
                              startIcon={<ContentCopyIcon />}
                              onClick={() =>
                                 void handleCopySocialLink(
                                    buildTrackedSocialCollectionUrl(
                                       socialSuccess.shareToken,
                                       utm,
                                       utm === 'email' ? 'share' : 'social',
                                    ),
                                 )
                              }
                           >
                              {label}
                           </Button>
                        </Grid>
                     ))}
                  </Grid>
                  <Typography variant='caption' color='text.secondary' display='block'>
                     Opens from these links are counted once per browser session. Plain links count
                     toward direct / untracked traffic.
                  </Typography>
               </Box>
            ) : (
               <>
                  {error && (
                     <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                     </Alert>
                  )}

                  {/* Step 1: Photo Selection */}
            {currentStep === 'photos' && (
               <Box>
                  <Box display='flex' alignItems='center' justifyContent='space-between' mb={2}>
                     <Box>
                        <Typography variant='h6'>
                           {preselectedPhotos && preselectedPhotos.length > 0
                              ? 'Selected Photos'
                              : 'Select Photos'}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                           {preselectedPhotos && preselectedPhotos.length > 0
                              ? `${selectedPhotos.length} photo${selectedPhotos.length !== 1 ? 's' : ''} ready to share`
                              : `${selectedPhotos.length} of ${photos.length} photos selected`}
                        </Typography>
                     </Box>
                     {!(preselectedPhotos && preselectedPhotos.length > 0) && (
                        <Button
                           variant='outlined'
                           onClick={selectAllPhotos}
                           size='small'
                           color={selectedPhotos.length > 0 ? 'secondary' : 'primary'}
                        >
                           {selectedPhotos.length === photos.length ? 'Deselect All' : 'Select All'}
                        </Button>
                     )}
                  </Box>

                  {photos.length === 0 ? (
                     <Box
                        display='flex'
                        flexDirection='column'
                        alignItems='center'
                        justifyContent='center'
                        py={4}
                        textAlign='center'
                     >
                        <Typography variant='h6' color='text.secondary' mb={1}>
                           {preselectedPhotos && preselectedPhotos.length > 0
                              ? 'No Photos Selected'
                              : 'No Photos Available'}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                           {preselectedPhotos && preselectedPhotos.length > 0
                              ? 'All photos have been removed. Please select photos to share.'
                              : `Upload some photos to this ${entityType} first to share them.`}
                        </Typography>
                     </Box>
                  ) : (
                     <Grid container spacing={1} sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {photos.map((photo) => {
                           const isSelected = selectedPhotos.some((p) => p.id === photo.id);
                           const isPreselectedMode =
                              preselectedPhotos && preselectedPhotos.length > 0;
                           return (
                              <Grid item xs={6} sm={4} md={3} key={photo.id}>
                                 <Card
                                    sx={{
                                       position: 'relative',
                                       cursor: isPreselectedMode ? 'default' : 'pointer',
                                       border: isSelected
                                          ? '3px solid #1976d2'
                                          : '1px solid #e0e0e0',
                                       '&:hover': {
                                          boxShadow: 3,
                                          transform: isPreselectedMode
                                             ? 'none'
                                             : 'translateY(-2px)',
                                          border: isSelected
                                             ? '3px solid #1976d2'
                                             : isPreselectedMode
                                               ? '1px solid #e0e0e0'
                                               : '2px solid #1976d2',
                                       },
                                       transition: 'all 0.2s ease',
                                       borderRadius: 2,
                                    }}
                                    onClick={() =>
                                       !isPreselectedMode && togglePhotoSelection(photo)
                                    }
                                 >
                                    <CardMedia
                                       component='img'
                                       height='120'
                                       image={photo.thumbnailUrl || photo.url}
                                       alt={photo.title}
                                       sx={{
                                          objectFit: 'cover',
                                          opacity: 1,
                                          transition: 'opacity 0.2s ease',
                                       }}
                                    />

                                    {!isPreselectedMode && (
                                       <>
                                          {/* Selection Overlay */}
                                          {isSelected && (
                                             <Box
                                                position='absolute'
                                                top={0}
                                                left={0}
                                                right={0}
                                                bottom={0}
                                                bgcolor='rgba(25, 118, 210, 0.2)'
                                                display='flex'
                                                alignItems='center'
                                                justifyContent='center'
                                                sx={{ pointerEvents: 'none' }}
                                             >
                                                <Box
                                                   bgcolor='#1976d2'
                                                   borderRadius='50%'
                                                   p={0.5}
                                                   display='flex'
                                                   alignItems='center'
                                                   justifyContent='center'
                                                >
                                                   <Checkbox
                                                      checked={true}
                                                      size='small'
                                                      sx={{
                                                         color: 'white',
                                                         p: 0,
                                                         '&.Mui-checked': {
                                                            color: 'white',
                                                         },
                                                      }}
                                                   />
                                                </Box>
                                             </Box>
                                          )}

                                          {/* Checkbox in top-left corner */}
                                          <Box
                                             position='absolute'
                                             top={4}
                                             left={4}
                                             bgcolor={isSelected ? '#1976d2' : 'rgba(0,0,0,0.6)'}
                                             borderRadius='50%'
                                             p={0.3}
                                             sx={{
                                                transition: 'all 0.2s ease',
                                                border: isSelected
                                                   ? '2px solid white'
                                                   : '2px solid transparent',
                                             }}
                                          >
                                             <Checkbox
                                                checked={isSelected}
                                                onChange={(e) => {
                                                   e.stopPropagation();
                                                   togglePhotoSelection(photo);
                                                }}
                                                size='small'
                                                sx={{
                                                   color: 'white',
                                                   p: 0,
                                                   '&.Mui-checked': {
                                                      color: 'white',
                                                   },
                                                }}
                                             />
                                          </Box>

                                          {/* Selection count badge */}
                                          {isSelected && (
                                             <Box
                                                position='absolute'
                                                top={4}
                                                right={4}
                                                bgcolor='#1976d2'
                                                color='white'
                                                borderRadius='50%'
                                                width={24}
                                                height={24}
                                                display='flex'
                                                alignItems='center'
                                                justifyContent='center'
                                                sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}
                                             >
                                                {selectedPhotos.findIndex(
                                                   (p) => p.id === photo.id
                                                ) + 1}
                                             </Box>
                                          )}
                                       </>
                                    )}

                                    {/* Remove button for preselected photos */}
                                    {isPreselectedMode && (
                                       <Button
                                          variant='contained'
                                          color='error'
                                          size='small'
                                          sx={{
                                             position: 'absolute',
                                             top: 4,
                                             right: 4,
                                             minWidth: 'auto',
                                             width: 28,
                                             height: 28,
                                             borderRadius: '50%',
                                             p: 0,
                                          }}
                                          onClick={(e) => {
                                             e.stopPropagation();
                                             togglePhotoSelection(photo);
                                          }}
                                       >
                                          ×
                                       </Button>
                                    )}

                                    {photo.title && (
                                       <Box
                                          p={1}
                                          bgcolor='rgba(0,0,0,0.7)'
                                          position='absolute'
                                          bottom={0}
                                          left={0}
                                          right={0}
                                       >
                                          <Typography
                                             variant='caption'
                                             noWrap
                                             sx={{ color: 'white', fontSize: '0.7rem' }}
                                          >
                                             {photo.title}
                                          </Typography>
                                       </Box>
                                    )}
                                 </Card>
                              </Grid>
                           );
                        })}
                     </Grid>
                  )}
               </Box>
            )}

            {/* Step 2: Layout Selection */}
            {currentStep === 'layout' && (
               <Box>
                  <Typography variant='h6' mb={0.5}>
                     Choose Layout ({selectedPhotos.length} photos selected)
                  </Typography>
                  <Typography variant='body2' color='text.secondary' mb={2}>
                     Next: preview how it will look, then create a link for Instagram, WhatsApp,
                     Messages, or SMS (or send by email).
                  </Typography>

                  <Grid container spacing={3} sx={{ maxHeight: '500px', overflowY: 'auto' }}>
                     {LAYOUT_OPTIONS.map((layout) => {
                        const validation = PhotoSharingService.validatePhotoCount(
                           layout.id,
                           selectedPhotos.length
                        );
                        const isDisabled = !validation.valid;
                        const isSelected = selectedLayout === layout.id;

                        return (
                           <Grid item xs={12} sm={6} md={4} key={layout.id}>
                              <Card
                                 sx={{
                                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                                    opacity: isDisabled ? 0.5 : 1,
                                    borderWidth: isSelected ? 3 : 1,
                                    borderStyle: 'solid',
                                    borderColor: isSelected
                                       ? theme.palette.primary.main
                                       : theme.palette.divider,
                                    bgcolor: 'background.paper',
                                    '&:hover': !isDisabled ? { boxShadow: theme.shadows[4] } : {},
                                    minHeight: '240px', // Ensure consistent card height
                                    display: 'flex',
                                    flexDirection: 'column',
                                 }}
                                 onClick={() => !isDisabled && setSelectedLayout(layout.id)}
                              >
                                 {/* Show selected photos in this layout preview */}
                                 <Box
                                    sx={{
                                       height: '160px',
                                       display: 'grid',
                                       gap: 1,
                                       p: 1,
                                       gridTemplateColumns: getLayoutGridConfig(layout.id).columns,
                                       gridTemplateRows: getLayoutGridConfig(layout.id).rows,
                                       overflow: 'hidden',
                                    }}
                                 >
                                    {selectedPhotos
                                       .slice(0, Math.min(4, getMaxPhotosForLayout(layout.id)))
                                       .map((photo, index) => (
                                          <Box
                                             key={photo.id}
                                             sx={{
                                                borderRadius: 1,
                                                overflow: 'hidden',
                                                minHeight: 0, // Allow grid to control height
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                             }}
                                          >
                                             <img
                                                src={photo.thumbnailUrl || photo.url}
                                                alt={photo.title || `Photo ${index + 1}`}
                                                style={{
                                                   width: '100%',
                                                   height: '100%',
                                                   objectFit: 'cover',
                                                }}
                                             />
                                          </Box>
                                       ))}
                                    {/* Show placeholder if not enough photos */}
                                    {Array.from({
                                       length: Math.max(
                                          0,
                                          Math.min(4, getMaxPhotosForLayout(layout.id)) -
                                             selectedPhotos.length
                                       ),
                                    }).map((_, index) => (
                                       <Box
                                          key={`placeholder-${index}`}
                                          sx={{
                                             borderRadius: 1,
                                             backgroundColor: theme.palette.action.hover,
                                             display: 'flex',
                                             alignItems: 'center',
                                             justifyContent: 'center',
                                             minHeight: 0, // Allow grid to control height
                                             border: `1px dashed ${theme.palette.divider}`,
                                          }}
                                       >
                                          <Typography variant='caption' color='text.secondary'>
                                             +
                                          </Typography>
                                       </Box>
                                    ))}
                                 </Box>
                                 <Box p={1}>
                                    <Typography variant='subtitle2' noWrap>
                                       {layout.name}
                                    </Typography>
                                    <Typography variant='caption' color='text.secondary'>
                                       {layout.description}
                                    </Typography>
                                    {layout.minPhotos && layout.maxPhotos && (
                                       <Typography
                                          variant='caption'
                                          display='block'
                                          color='text.secondary'
                                       >
                                          {layout.minPhotos}-{layout.maxPhotos} photos
                                       </Typography>
                                    )}
                                    {isDisabled && validation.message && (
                                       <Typography variant='caption' color='error' display='block'>
                                          {validation.message}
                                       </Typography>
                                    )}
                                    {layout.id === 'masonry-pinterest' && (
                                       <Typography
                                          variant='caption'
                                          color='text.secondary'
                                          display='block'
                                          sx={{ mt: 0.5, fontStyle: 'italic' }}
                                       >
                                          Preview uses a simple grid; the shared page uses a
                                          staggered gallery when supported.
                                       </Typography>
                                    )}
                                 </Box>
                              </Card>
                           </Grid>
                        );
                     })}
                  </Grid>
               </Box>
            )}

            {/* Step 3: Preview Layout */}
            {currentStep === 'preview' && (
               <Box>
                  <Typography variant='h6' mb={2}>
                     Preview Layout
                  </Typography>
                  <Typography variant='body2' color='text.secondary' mb={3}>
                     This is how your {selectedLayout.replace('-', ' ')} layout will look with{' '}
                     {selectedPhotos.length} photo{selectedPhotos.length !== 1 ? 's' : ''}
                  </Typography>

                  <Box
                     sx={{
                        border: 2,
                        borderColor: 'divider',
                        borderRadius: 2,
                        p: 2,
                        bgcolor: 'action.hover',
                        maxHeight: '400px', // More compact
                        overflow: 'auto',
                        maxWidth: '600px', // Limit width for better preview
                        mx: 'auto', // Center the preview
                     }}
                  >
                     {/* Layout Preview */}
                     <Box
                        sx={{
                           display: 'grid',
                           gap: 2,
                           gridTemplateColumns: getLayoutGridConfig(selectedLayout).columns,
                           gridTemplateRows: getLayoutGridConfig(selectedLayout).rows,
                           maxWidth: '100%',
                           minHeight: '200px', // Compact height
                        }}
                     >
                        {selectedPhotos
                           .slice(0, getMaxPhotosForLayout(selectedLayout))
                           .map((photo, index) => (
                              <Box
                                 key={photo.id}
                                 sx={{
                                    position: 'relative',
                                    aspectRatio:
                                       selectedLayout === 'single-hero'
                                          ? '4/3'
                                          : selectedLayout === 'story-horizontal'
                                            ? '9/16'
                                            : selectedLayout === 'magazine-editorial'
                                              ? '3/2'
                                              : '1/1',
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    boxShadow: 1,
                                 }}
                              >
                                 <img
                                    src={photo.thumbnailUrl || photo.url}
                                    alt={photo.title || `Photo ${index + 1}`}
                                    style={{
                                       width: '100%',
                                       height: '100%',
                                       objectFit: 'cover',
                                    }}
                                 />
                                 {photo.title && (
                                    <Box
                                       sx={{
                                          position: 'absolute',
                                          bottom: 0,
                                          left: 0,
                                          right: 0,
                                          background:
                                             'linear-gradient(transparent, rgba(0,0,0,0.7))',
                                          color: 'white',
                                          p: 1,
                                       }}
                                    >
                                       <Typography variant='caption' noWrap>
                                          {photo.title}
                                       </Typography>
                                    </Box>
                                 )}
                              </Box>
                           ))}
                     </Box>

                     {selectedPhotos.length > getMaxPhotosForLayout(selectedLayout) && (
                        <Typography
                           variant='caption'
                           color='text.secondary'
                           sx={{ mt: 2, display: 'block' }}
                        >
                           Note: Only the first {getMaxPhotosForLayout(selectedLayout)} photos will
                           be included in this layout.
                        </Typography>
                     )}
                  </Box>

                  {/* Layout Info */}
                  <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                     <Typography variant='body2' color='text.secondary'>
                        <strong>Layout:</strong>{' '}
                        {LAYOUT_OPTIONS.find((l) => l.id === selectedLayout)?.name ||
                           selectedLayout}
                     </Typography>
                     <Typography variant='body2' color='text.secondary'>
                        <strong>Description:</strong>{' '}
                        {LAYOUT_OPTIONS.find((l) => l.id === selectedLayout)?.description ||
                           'Custom layout'}
                     </Typography>
                     <Typography variant='body2' color='text.secondary'>
                        <strong>Photos:</strong>{' '}
                        {Math.min(selectedPhotos.length, getMaxPhotosForLayout(selectedLayout))} of{' '}
                        {selectedPhotos.length} selected will be included
                     </Typography>
                  </Box>
               </Box>
            )}

            {/* Step 4: Recipients Selection */}
            {currentStep === 'recipients' && (
               <Box>
                  <Tabs
                     value={shareDelivery}
                     onChange={(_, v) => setShareDelivery(v as 'social' | 'email')}
                     variant='fullWidth'
                     sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                  >
                     <Tab
                        value='social'
                        label='Social link'
                        icon={<LinkIcon />}
                        iconPosition='start'
                     />
                     <Tab value='email' label='Email' icon={<EmailIcon />} iconPosition='start' />
                  </Tabs>

                  {shareDelivery === 'social' && (
                     <Alert severity='info' sx={{ mb: 2 }}>
                        Create a link to paste in Instagram, WhatsApp, Messages, or SMS. Only the
                        photos you selected are included.
                     </Alert>
                  )}

                  {shareDelivery === 'email' && (
                     <>
                        <Typography variant='h6' mb={2}>
                           Select Recipients
                        </Typography>

                        <Paper sx={{ mb: 3 }}>
                           <Tabs
                              value={recipientTab}
                              onChange={(_, newValue) => setRecipientTab(newValue)}
                              variant='fullWidth'
                           >
                        <Tab
                           icon={<PeopleIcon />}
                           label={`Participants (${participants.length})`}
                           iconPosition='start'
                        />
                        <Tab
                           icon={<EmailIcon />}
                           label={`Others (${nonParticipants.length})`}
                           iconPosition='start'
                        />
                     </Tabs>

                     <TabPanel value={recipientTab} index={0}>
                        {participants.length > 0 && (
                           <Box
                              display='flex'
                              justifyContent='space-between'
                              alignItems='center'
                              mb={2}
                           >
                              <Typography variant='body2' color='text.secondary'>
                                 {selectedParticipants.size} of {participants.length} participants
                                 selected
                              </Typography>
                              <Box>
                                 <Button
                                    size='small'
                                    onClick={
                                       selectedParticipants.size === participants.length
                                          ? deselectAllParticipants
                                          : selectAllParticipants
                                    }
                                    variant='outlined'
                                 >
                                    {selectedParticipants.size === participants.length
                                       ? 'Deselect All'
                                       : 'Select All'}
                                 </Button>
                              </Box>
                           </Box>
                        )}

                        <Box>
                           {participants.length === 0 ? (
                              <Typography color='text.secondary' textAlign='center' py={2}>
                                 No participants found
                              </Typography>
                           ) : (
                              <FormControl fullWidth sx={{ mb: 2 }}>
                                 <InputLabel id='participant-select-label'>
                                    Select Participants
                                 </InputLabel>
                                 <Select
                                    labelId='participant-select-label'
                                    multiple
                                    value={Array.from(selectedParticipants)}
                                    onChange={(event) => {
                                       const value = event.target.value;
                                       setSelectedParticipants(
                                          new Set(
                                             typeof value === 'string' ? value.split(',') : value
                                          )
                                       );
                                    }}
                                    input={<OutlinedInput label='Select Participants' />}
                                    renderValue={(selected) => (
                                       <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                          {selected.map((value) => {
                                             const participant = participants.find(
                                                (p) => p.id === value
                                             );
                                             return (
                                                <Chip
                                                   key={value}
                                                   label={
                                                      participant
                                                         ? PhotoSharingService.formatRecipientName(
                                                              participant
                                                           )
                                                         : value
                                                   }
                                                   size='small'
                                                   sx={{ maxWidth: '150px' }}
                                                />
                                             );
                                          })}
                                       </Box>
                                    )}
                                 >
                                    {participants.map((participant) => (
                                       <MenuItem key={participant.id} value={participant.id}>
                                          <Checkbox
                                             checked={selectedParticipants.has(participant.id)}
                                          />
                                          <Box sx={{ ml: 1, flex: 1 }}>
                                             <ListItemText
                                                primary={PhotoSharingService.formatRecipientName(
                                                   participant
                                                )}
                                                secondary={
                                                   <Box
                                                      sx={{
                                                         display: 'flex',
                                                         alignItems: 'center',
                                                         gap: 1,
                                                      }}
                                                   >
                                                      <Typography
                                                         variant='caption'
                                                         color='text.secondary'
                                                      >
                                                         {participant.email}
                                                      </Typography>
                                                      {participant.role && (
                                                         <Chip
                                                            label={participant.role}
                                                            size='small'
                                                            variant='outlined'
                                                            sx={{
                                                               height: '16px',
                                                               fontSize: '10px',
                                                            }}
                                                         />
                                                      )}
                                                   </Box>
                                                }
                                             />
                                          </Box>
                                       </MenuItem>
                                    ))}
                                 </Select>
                              </FormControl>
                           )}
                        </Box>
                     </TabPanel>

                     <TabPanel value={recipientTab} index={1}>
                        <Box mb={2}>
                           <Typography variant='body2' color='text.secondary' mb={2}>
                              Add people who are not participants in this {entityType}
                           </Typography>

                           <Box display='flex' gap={1} mb={2}>
                              <TextField
                                 label='Email Address *'
                                 value={newEmail}
                                 onChange={(e) => setNewEmail(e.target.value)}
                                 size='small'
                                 type='email'
                                 sx={{ flex: 1 }}
                                 error={Boolean(
                                    newEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)
                                 )}
                                 helperText={
                                    newEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)
                                       ? 'Please enter a valid email address'
                                       : ''
                                 }
                              />
                              <TextField
                                 label='Display Name'
                                 value={newDisplayName}
                                 onChange={(e) => setNewDisplayName(e.target.value)}
                                 size='small'
                                 sx={{ flex: 1 }}
                                 placeholder='Optional'
                              />
                              <Button
                                 variant='contained'
                                 onClick={addNonParticipant}
                                 startIcon={<PersonAddIcon />}
                                 disabled={
                                    !newEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)
                                 }
                              >
                                 Add
                              </Button>
                           </Box>
                        </Box>

                        <Box sx={{ maxHeight: '150px', overflowY: 'auto' }}>
                           {nonParticipants.length === 0 ? (
                              <Typography color='text.secondary' textAlign='center' py={2}>
                                 No additional recipients added yet
                              </Typography>
                           ) : (
                              <Box>
                                 <Typography variant='body2' color='text.secondary' mb={1}>
                                    {nonParticipants.length} additional recipient
                                    {nonParticipants.length !== 1 ? 's' : ''} added:
                                 </Typography>
                                 {nonParticipants.map((recipient) => (
                                    <Box
                                       key={recipient.id}
                                       display='flex'
                                       alignItems='center'
                                       mb={1}
                                       sx={{
                                          p: 1,
                                          borderRadius: 1,
                                          backgroundColor: '#f8f9fa',
                                          border: '1px solid #e9ecef',
                                       }}
                                    >
                                       <Box flex={1}>
                                          <Typography variant='body2' fontWeight={500}>
                                             {recipient.displayName || 'No name provided'}
                                          </Typography>
                                          <Typography variant='caption' color='text.secondary'>
                                             {recipient.email}
                                          </Typography>
                                       </Box>
                                       <IconButton
                                          size='small'
                                          onClick={() => removeNonParticipant(recipient.id)}
                                          color='error'
                                          title='Remove recipient'
                                       >
                                          <DeleteIcon fontSize='small' />
                                       </IconButton>
                                    </Box>
                                 ))}
                              </Box>
                           )}
                        </Box>
                     </TabPanel>
                        </Paper>
                     </>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/* Message and Options */}
                  <Box>
                     <TextField
                        label={shareDelivery === 'social' ? 'Collection title (optional)' : 'Subject'}
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        fullWidth
                        margin='normal'
                        size='small'
                     />
                     <TextField
                        label='Personal Message (optional)'
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        fullWidth
                        multiline
                        rows={3}
                        margin='normal'
                        size='small'
                     />
                  </Box>
               </Box>
            )}
               </>
            )}
         </DialogContent>

         <Snackbar
            open={copyToastOpen}
            autoHideDuration={4000}
            onClose={() => setCopyToastOpen(false)}
            message='Copied — paste in Instagram, WhatsApp, or Messages.'
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
         />

         <DialogActions sx={{ p: 2 }}>
            {socialSuccess ? (
               <>
                  <Box flex={1} />
                  <Button onClick={handleClose} color='inherit'>
                     Close
                  </Button>
                  <Button variant='contained' onClick={handleDoneSocialSuccess} color='primary'>
                     Done
                  </Button>
               </>
            ) : (
               <>
                  {currentStep !== 'photos' && (
                     <Button onClick={handleBack} disabled={loading}>
                        Back
                     </Button>
                  )}

                  <Box flex={1} />

                  <Button onClick={handleClose} disabled={loading}>
                     Cancel
                  </Button>

                  {currentStep !== 'recipients' ? (
                     <Button variant='contained' onClick={handleNext} disabled={loading}>
                        {currentStep === 'photos'
                           ? 'Choose Layout'
                           : currentStep === 'layout'
                             ? 'Preview layout'
                             : currentStep === 'preview'
                               ? 'Select Recipients'
                               : 'Next'}
                     </Button>
                  ) : (
                     <Button
                        variant='contained'
                        onClick={handleShare}
                        disabled={loading}
                        startIcon={
                           loading ? (
                              <CircularProgress size={20} />
                           ) : shareDelivery === 'social' ? (
                              <LinkIcon />
                           ) : (
                              <SendIcon />
                           )
                        }
                     >
                        {loading
                           ? shareDelivery === 'social'
                              ? 'Creating link...'
                              : 'Sharing...'
                           : shareDelivery === 'social'
                             ? 'Create share link'
                             : 'Share Photos'}
                     </Button>
                  )}
               </>
            )}
         </DialogActions>
      </Dialog>
   );
};

export default PhotoSharingModal;
